import { absUrl } from "./media";
import { Star, ExternalLink, Share2 } from "lucide-react";
import * as Icons from "lucide-react";
import { shareLink } from "./TourStopsBlock";
import { renderInline } from "./StoryChapterBlock";

const HeroBlock = ({ block, festival }) => {
  const cfg = block.config || {};
  const bg = cfg.background || "#0A1626";
  const logoImg = cfg.logo && cfg.logo_style === "badge" ? (
    <img src={absUrl(cfg.logo)} alt={`${block.title || festival?.title || ""} logo`} className="block h-20 w-20 sm:h-24 sm:w-24 object-cover rounded-2xl bg-white p-1.5 shadow-2xl ring-2 ring-[#F2CC8F]/60 mb-5" data-testid="hero-logo" />
  ) : cfg.logo ? (
    <img src={absUrl(cfg.logo)} alt={`${block.title || festival?.title || ""} logo`} className="block w-full max-w-[26rem] sm:max-w-[34rem] h-auto rounded-2xl bg-white p-3 sm:p-4 shadow-2xl mb-6" data-testid="hero-logo" />
  ) : null;
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background: cfg.hero_image
          ? `linear-gradient(rgba(10,22,38,0.55), rgba(27,58,92,0.75)), url(${cfg.hero_image}) center/cover`
          : `linear-gradient(165deg, ${bg} 0%, #10233A 55%, #1B3A5C 100%)`,
      }}
      data-testid="fest-hero"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 text-white">
        <div className="inline-flex items-center gap-2 text-[#F2CC8F] uppercase tracking-[0.3em] text-xs mb-3">
          <Star className="h-4 w-4" fill="#F2CC8F" />
          {cfg.eyebrow || "Festival"}
        </div>
        <h1 className="font-['Playfair_Display'] text-[clamp(1.75rem,9vw,2.25rem)] sm:text-5xl md:text-6xl font-semibold mb-3 max-w-4xl leading-tight">
          {cfg.title_first && <span className="block mb-6">{block.title || festival?.title}</span>}
          {cfg.banner_image && (
          <img src={absUrl(cfg.banner_image)} alt="" className="block w-full max-w-[26rem] sm:max-w-[34rem] h-auto rounded-2xl shadow-2xl mb-6 ring-1 ring-white/15" data-testid="hero-banner-image" />
        )}
          {cfg.logo && !cfg.logo_below_text && logoImg}
        {!cfg.title_first && (block.title || festival?.title)}
        </h1>
        {(block.quip || festival?.motto || festival?.tagline) && (
          <p className="text-lg md:text-2xl text-white/90 italic max-w-3xl leading-relaxed [&_strong]:text-white [&_strong]:font-extrabold [&_strong]:not-italic">
            {renderInline(block.quip || festival?.motto || festival?.tagline, "#F2CC8F")}
          </p>
        )}
        {cfg.logo && cfg.logo_below_text && <div className="mt-6">{logoImg}</div>}
        {(cfg.facts || []).filter((f) => f.value).length > 0 && (
          <ul className="m-0 p-0 list-none mt-7 grid grid-cols-1 sm:flex sm:flex-wrap gap-2.5 max-w-5xl" data-testid="fest-hero-facts">
            {cfg.facts.filter((f) => f.value).map((f, i) => {
              const Icon = Icons[f.icon] || Icons.Info;
              const inner = (
                <>
                  <span className="h-9 w-9 shrink-0 rounded-xl bg-[#F2CC8F] text-[#0A1626] flex items-center justify-center"><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0">
                    <span className="block text-[10px] uppercase tracking-[0.22em] font-bold text-[#F2CC8F]">{f.label}</span>
                    <span className="block text-sm font-semibold text-white leading-snug break-words">{f.value}</span>
                  </span>
                </>
              );
              const cls = "flex items-center gap-3 rounded-2xl bg-black/35 backdrop-blur-md border border-white/15 pl-2 pr-4 py-2 transition-colors";
              return (
                <li key={i} data-testid={`fest-hero-fact-${i}`}>
                  {f.url
                    ? <a href={f.url} target={/^https?:\/\//i.test(f.url) ? "_blank" : undefined} rel="noopener noreferrer" className={`${cls} hover:bg-black/55 hover:border-[#F2CC8F]/60`}>{inner}</a>
                    : <div className={cls}>{inner}</div>}
                </li>
              );
            })}
          </ul>
        )}
        {(cfg.start_date || festival?.start_date) && !festival?.evergreen && (festival?.kind || "event") === "event" && (
          <p className="text-sm md:text-base text-[#F2CC8F] uppercase tracking-widest mt-6">
            {cfg.start_date || festival?.start_date}
            {(cfg.end_date || festival?.end_date) && cfg.end_date !== cfg.start_date && (
              <> — {cfg.end_date || festival?.end_date}</>
            )}
            {festival?.home_city && <> · {festival.home_city}</>}
          </p>
        )}
        {(cfg.button_label && cfg.button_url) || cfg.show_share ? (
          <div className="mt-6 flex flex-wrap gap-3" data-testid="fest-hero-buttons">
            {cfg.show_share && (
              <button
                type="button"
                onClick={() => shareLink({ title: block.title || festival?.title, text: block.quip || festival?.tagline || "", url: window.location.href.split("#")[0] })}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold border border-white/40 bg-white/10 text-white hover:bg-white/20 transition-colors"
                data-testid="fest-hero-share"
              >
                <Share2 className="h-4 w-4" /> Share this page
              </button>
            )}
            {cfg.button_label && cfg.button_url && (<>
            <a
              href={cfg.button_url}
              target={/^https?:\/\//i.test(cfg.button_url) ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold shadow-lg transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: cfg.button_color || "#F2CC8F", color: cfg.button_text_color || "#0A1626" }}
              data-testid="fest-hero-button"
            >
              {cfg.button_label} {/^https?:\/\//i.test(cfg.button_url) && <ExternalLink className="h-4 w-4" />}
            </a>
            {cfg.button2_label && cfg.button2_url && (
              <a
                href={cfg.button2_url}
                target={/^https?:\/\//i.test(cfg.button2_url) ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold border border-white/40 bg-white/10 text-white hover:bg-white/20 transition-colors"
                data-testid="fest-hero-button-2"
              >
                {cfg.button2_label}
              </a>
            )}
            </>)}
          </div>
        ) : null}
        {(cfg.socials || []).filter((l) => l.url).length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="fest-hero-socials">
            {cfg.socials.filter((l) => l.url).map((l, i) => {
              const Icon = Icons[l.icon] || Icons.Link;
              return (
                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 hover:bg-white/25 text-white px-3.5 py-2 text-sm font-semibold transition-colors" data-testid={`fest-hero-social-${i}`}>
                  <Icon className="h-4 w-4" style={{ color: l.color || "#F2CC8F" }} /> {l.label}
                </a>
              );
            })}
          </div>
        )}
      </div>
      {(cfg.section_links || []).filter((l) => l.url && l.label).length > 0 && (
        <nav className="relative border-t border-white/10 bg-black/30 backdrop-blur" aria-label="Jump to a section" data-testid="fest-hero-section-nav">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex gap-2 overflow-x-auto no-scrollbar">
            {cfg.section_links.filter((l) => l.url && l.label).map((l, i) => (
              <a key={i} href={l.url} className="shrink-0 rounded-full border border-white/25 text-white/90 hover:bg-white hover:text-[#0A1626] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors" data-testid={`fest-hero-section-${i}`}>{l.label}</a>
            ))}
          </div>
        </nav>
      )}
    </section>
  );
};

export default HeroBlock;
